import {
  KeyboardEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { styled } from '@mui/material'
import { Element, Text } from 'html-react-parser'
import type { DOMNode } from 'html-react-parser'
import { focusStyle } from '@app/theme'

const getDomNodeText = (node?: DOMNode): string => {
  if (!node) return ''
  if (node instanceof Text) return node.data
  if (node instanceof Element) {
    return node.children.map(child => getDomNodeText(child as DOMNode)).join('')
  }
  return ''
}

// a11y: name the scroll region from the table's aria-label (which also wins the table's own
// computed accessible name) when present, else its caption, else a generic fallback — so the
// group and the table agree.
export const getTableAccessibleName = (tableNode: Element): string => {
  const captionNode = tableNode.children.find(
    (child): child is Element => child instanceof Element && child.name === 'caption'
  )
  const captionText = getDomNodeText(captionNode).trim()
  const ariaLabel = (tableNode.attribs?.['aria-label'] || '').trim()
  return ariaLabel || captionText || 'Table'
}

// a11y: inline-block so a normal-width table keeps its shrink-to-fit layout, while a table wider
// than the container is capped and scrolls sideways. Only the overflowing (focusable) state adds
// the ring margin + focus ring, so a non-overflowing table is left visually unchanged. SC 2.4.7.
const ScrollBox = styled('div')(({ theme }) => ({
  display: 'inline-block',
  verticalAlign: 'top',
  maxWidth: '100%',
  overflowX: 'auto',
  '&[tabindex]': {
    margin: '3px',
    '&:focus-visible': {
      ...focusStyle(theme).shadow,
    },
  },
}))

interface TableScrollContainerProps {
  accessibleName: string
  onKeyDown: KeyboardEventHandler<HTMLElement>
  children: ReactNode
}

// a11y: make the table's scroll region a focusable, keyboard-scrollable group ONLY while the table
// actually overflows its container — a normal-width table stays a plain wrapper (no tab stop, role,
// name, or ring), per qti-pwa#244. Overflow is measured from rendered rects (Safari under-reports
// scrollWidth, the very bug the arrow handler works around) and re-checked on resize. SC 2.1.1.
export const TableScrollContainer = ({
  accessibleName,
  onKeyDown,
  children,
}: TableScrollContainerProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const box = boxRef.current
    const table = box?.querySelector('table')
    if (!box || !table) return

    const measure = () => {
      const maxScrollLeft =
        box.scrollLeft +
        Math.ceil(table.getBoundingClientRect().right - box.getBoundingClientRect().right)
      setOverflows(maxScrollLeft > 0)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(box)
    observer.observe(table)
    return () => observer.disconnect()
  }, [children])

  return (
    <ScrollBox
      ref={boxRef}
      {...(overflows && {
        role: 'group',
        tabIndex: 0,
        'aria-label': accessibleName,
        onKeyDown,
      })}
    >
      {children}
    </ScrollBox>
  )
}
